import GmapComponent from "./GmapComponent";
import loadGoogleMaps from "./utils/loadGoogleMaps";
import loadMarkerClusterer from "./utils/loadMarkerClusterer";
export default class DealerLocator {
    // In constructor, only keep minZoom
    // In the constructor, add translations for days
    constructor(options = {}) {
        this.options = {
            icon: window.dealerLocatorData?.markerIcon || window.dealerLocatorData?.pluginUrl + 'assets/images/marker.png',
            activeIcon: window.dealerLocatorData?.pluginUrl + 'assets/images/marker-active.png',
            zoom: 8,
            minZoom: 8,
            maxZoom: 15,
            apiKey: window.dealerLocatorData?.mapApi,
            maxResults: 100,
            maxDistance: 20,
            styles: window.dealerLocatorData?.mapTheme || []
        };
        this.options = Object.assign(this.options, options);
        this.map = null;
        this.gMap = null;
        this.markers = [];
        this.infowindows = [];
        this.bounds = null;
        this.markerCluster = null;
        this.currentPosition = null;
        
        this._init();
    }

    async _init() {
        try {
            await loadGoogleMaps(this.options.apiKey);
            await loadMarkerClusterer();
            // Read dataset flags from DOM
            const mapElement = document.querySelector('[data-gmap]');
            const ds = mapElement ? mapElement.dataset : {};
            this.options.hideSearch = ds.hideSearch === 'true';
            this.options.lockMap = ds.lockMap === 'true';
            this.options.disableCluster = ds.disableCluster === 'true';

            this.initMap();
            if (!this.options.hideSearch) {
                this.initAutocomplete(); // Add autocomplete initialization
            }
            // Extra flags
            this.options.hideSearchInArea = ds.hideSearchInArea === 'true';
            this.options.hideGeoloc = ds.hideGeoloc === 'true';
            this.options.zoomOnMarkerClick = ds.zoomOnMarkerClick === 'true';
            this.options.openInfoOnMarkerClick = ds.openInfoOnMarkerClick !== 'false';
            this.options.zoomOnSelect = ds.zoomOnSelect !== 'false';
            this.options.openInfoOnSelect = ds.openInfoOnSelect !== 'false';
            this.loadInitialLocations(); // Load initial locations
            this.initGeolocation();
            this.initSearchInArea();
            this.initDealersList();
        } catch (error) {
            console.error('Failed to load Google Maps:', error);
        }
    }

    // Add this new method
    // Modify loadInitialLocations to use loadLocations instead of loadLocationsInView
    loadInitialLocations() {
        const mapElement = document.querySelector('[data-gmap]');
        const initialLat = parseFloat(mapElement.dataset.lat);
        const initialLng = parseFloat(mapElement.dataset.lng);
        
        this.loadLocations({
            lat: initialLat,
            lng: initialLng
        });
    }

    // Add autocomplete initialization
    initAutocomplete() {
        const input = document.getElementById('search-location');
        if (!input) return;
    
        const searchContainer = input.closest('.dealer-locator__search');
        const autocomplete = new google.maps.places.Autocomplete(input);
        autocomplete.bindTo('bounds', this.gMap);
    
        // Move pac-container inside search container when it's created
        const observer = new MutationObserver((mutations, obs) => {
            const pacContainer = document.querySelector('.pac-container');
            if (pacContainer) {
                searchContainer.appendChild(pacContainer);
                obs.disconnect(); // Stop observing once we've moved the container
            }
        });
    
        // Start observing the document body for the pac-container
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    
        // Add form submit handler
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                const pacItem = document.querySelector('.pac-item');
                if (pacItem) {
                    // Get text from the first pac-item and force update input
                    const mainText = pacItem.querySelector('.pac-item-query')?.textContent || '';
                    const secondaryText = pacItem.querySelector('.pac-item-query + span')?.textContent || '';
                    const fullAddress = `${mainText}${secondaryText ? `, ${secondaryText}` : ''}`.trim();
                    
                    // Force input update using setTimeout to ensure it happens after current event loop
                    setTimeout(() => {
                        input.value = fullAddress;
                        input.dispatchEvent(new Event('change', { bubbles: true }));
                    }, 0);
                    
                    const geocoder = new google.maps.Geocoder();
                    geocoder.geocode({ address: fullAddress }, (results, status) => {
                        if (status === 'OK' && results[0]) {
                            const place = results[0];
                            this.currentPosition = place.geometry.location;
                            this.gMap.setCenter(place.geometry.location);
                            this.gMap.setZoom(12);
                            
                            this.loadLocations({
                                lat: place.geometry.location.lat(),
                                lng: place.geometry.location.lng()
                            });
                        }
                    });
                }
            }
        });
    
        // Rest of the autocomplete listener remains the same
        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place || !place.geometry || !place.geometry.location) {
                console.warn('Place contains no geometry');
                return;
            }
    
            this.currentPosition = place.geometry.location;
            this.gMap.setCenter(place.geometry.location);
            this.gMap.setZoom(12);
    
            this.loadLocations({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
            });
        });
    }

    // Modify the addMarkers method to include website and opening hours
    // Remove the second addMarkers method and update the first one:
    // Add this helper method to the DealerLocator class
    // Fix the getOpeningStatus method
    getOpeningStatus(hours) {
        if (!hours) return null;
    
        const now = new Date();
        const currentDay = now.toLocaleDateString('fr-FR', { weekday: 'long' }).toLowerCase();
        const currentTime = now.getHours() * 60 + now.getMinutes();
    
        // Get today's hours
        const todayHours = hours[currentDay];
        if (!todayHours || todayHours.toLowerCase() === 'fermé') return { status: 'closed', text: 'Fermé' };
    
        // Parse opening hours (format: "10:00 – 19:30")
        const [openStr, closeStr] = todayHours.split('–').map(t => t.trim());
        const [openHour, openMin] = openStr.split(':').map(Number);
        const [closeHour, closeMin] = closeStr.split(':').map(Number);
    
        const openingTime = openHour * 60 + openMin;
        const closingTime = closeHour * 60 + closeMin;
    
        if (currentTime < openingTime) {
            return { status: 'closed', text: 'Fermé' };
        } else if (currentTime >= closingTime) {
            return { status: 'closed', text: 'Fermé' };
        } else if (currentTime >= closingTime - 60) {
            return { status: 'closing-soon', text: 'Ferme bientôt' };
        } else {
            return { status: 'open', text: 'Ouvert' };
        }
    }
    
    // Update the info window content in addMarkers method
    addMarkers(locations) {
        if (!locations || !locations.length) return;
        
        this.bounds = new google.maps.LatLngBounds();
    
        locations.forEach(location => {
            const position = {
                lat: parseFloat(location.Building.localization_lat),
                lng: parseFloat(location.Building.localization_lng)
            };
    
            const marker = new google.maps.Marker({
                position: position,
                map: this.gMap,
                icon: this.options.icon
            });
    
            marker.locationData = location;
            
            // Create website HTML if available
            const websiteHtml = location.Building.website ? 
                `<p class="website">
                    <a href="${location.Building.website}" data-barba-prevent="self" target="_blank" rel="noopener">
                        Voir le site web
                    </a>
                </p>` : '';
    
            // Create opening hours HTML if available
            let openingStatus = { status: '', text: '' };
            let hoursHtml = '';
            let statusHtml = ''; // Define statusHtml here
    
            // Dans la méthode addMarkers, modifions la partie du traitement des horaires
            if (location.Building.opening_hours) {
                try {
                    // Vérifions si c'est déjà un objet ou une chaîne JSON
                    const hours = typeof location.Building.opening_hours === 'string' 
                        ? JSON.parse(location.Building.opening_hours.replace(/\\/g, '').replace(/\u2009/g, ' '))
                        : location.Building.opening_hours;
                    
                    if (Object.keys(hours).length > 0) {
                        openingStatus = this.getOpeningStatus(hours);
                        statusHtml = openingStatus.status ? 
                            `<div class="status-indicator ${openingStatus.status}">${openingStatus.text}</div>` : '';
                        
                        // Create hours HTML
                        hoursHtml = '<div class="opening-hours"><h4>Horaires d\'ouverture</h4>';
                        Object.entries(hours).forEach(([day, time]) => {
                            hoursHtml += `
                                <div class="hours-row">
                                    <span class="day">${day}</span>: 
                                    <span class="time">${time}</span>
                                </div>`;
                        });
                        hoursHtml += '</div>';
                    }
                } catch (e) {
                    console.error('Error parsing opening hours:', e, location.Building.opening_hours);
                }
            }
    
            // Create photo HTML if available
            const photoHtml = location.Building.photo_url ? 
                `<div class="store-photo">
                    <img src="${location.Building.photo_url}" alt="${location.Building.title}">
                </div>` : '';
    
            const infowindow = new google.maps.InfoWindow({
                content: `
                    <div class="map-infowindow">
                        ${photoHtml}
                        <h3>${location.Building.title}</h3>
                        ${statusHtml}
                        <p class="address">
                            ${location.Building.street || ''}<br>
                            ${location.Building.postalcode || ''} ${location.Building.city || ''}
                        </p>
                        ${hoursHtml}
                    </div>
                `
            });
    
            marker.addListener('click', () => {
                if (this.options.zoomOnMarkerClick) {
                    this.gMap.setCenter(position);
                    this.gMap.setZoom(15);
                }
                if (this.options.openInfoOnMarkerClick) {
                    this.infowindows.forEach(iw => iw.close());
                    infowindow.open(this.gMap, marker);
                }
            });
    
            this.markers.push(marker);
            this.infowindows.push(infowindow);
            this.bounds.extend(position);
        });
    
        if (this.markers.length > 0) {
            this.gMap.fitBounds(this.bounds);
            
            // Initialize MarkerClusterer if available
            if (!this.options.disableCluster && typeof MarkerClusterer !== 'undefined') {
                this.markerCluster = new MarkerClusterer(this.gMap, this.markers, {
                    imagePath: 'https://unpkg.com/@google/markerclustererplus@4.0.1/images/m',
                    maxZoom: 15,
                    minimumClusterSize: 2,
                    gridSize: 50
                });
            }
        }
    }
    loadLocations(center) {
        document.querySelector('[data-gmap]').classList.add('is-loading');
        const bounds = this.gMap.getBounds();
    
        jQuery.ajax({
            url: document.querySelector('[data-gmap]').dataset.url,
            type: 'POST',
            data: {
                action: 'get_store_locations',
                center: center,
                bounds: bounds ? {
                    north: bounds.getNorthEast().lat(),
                    south: bounds.getSouthWest().lat(),
                    east: bounds.getNorthEast().lng(),
                    west: bounds.getSouthWest().lng()
                } : null,
                max_results: this.options.maxResults
            },
            success: (response) => {
                this.clearMarkers();
                // Clear the listing if no results
                if (!response.data || response.data.length === 0) {
                    const resultsContainer = document.querySelector('.dealer-locator__results');
                    if (resultsContainer) {
                        resultsContainer.innerHTML = '<div class="no-results">No locations found in this area</div>';
                    }
                } else {
                    const locations = this.sortLocationsByDistance(response.data, center);
                    this.addMarkers(locations.slice(0, this.options.maxResults));
                    this.updateClosestLocations();
                }
                document.querySelector('[data-gmap]').classList.remove('is-loading');
            }
        });
    }

    sortLocationsByDistance(locations, center) {
        return locations.sort((a, b) => {
            const distA = this.calculateDistance(
                { lat: parseFloat(a.Building.localization_lat), lng: parseFloat(a.Building.localization_lng) },
                center
            );
            const distB = this.calculateDistance(
                { lat: parseFloat(b.Building.localization_lat), lng: parseFloat(b.Building.localization_lng) },
                center
            );
            return distA - distB;
        });
    }

    calculateDistance(point1, point2) {
        const lat1 = point1.lat;
        const lon1 = point1.lng;
        const lat2 = point2.lat;
        const lon2 = point2.lng;
        
        const R = 6371; // Radius of the earth in km
        const dLat = this.deg2rad(lat2 - lat1);
        const dLon = this.deg2rad(lon2 - lon1);
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
            Math.sin(dLon/2) * Math.sin(dLon/2); 
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
        return R * c; // Distance in km
    }

    deg2rad(deg) {
        return deg * (Math.PI/180);
    }

    initSearchInArea() {
        const searchButton = document.createElement('button');
        searchButton.id = 'map-zone-search';
        searchButton.innerHTML = 'Search in this area';
        searchButton.className = 'map-control-button';
        searchButton.style.display = 'none';
        
        searchButton.addEventListener('click', () => {
            const center = this.gMap.getCenter();
            this.loadLocations({
                lat: center.lat(),
                lng: center.lng()
            });
            searchButton.style.display = 'none';
        });
    
        this.gMap.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(searchButton);
    
        // Show button when map is moved
        google.maps.event.addListener(this.gMap, 'dragend', () => {
            if (!this.options.hideSearchInArea) searchButton.style.display = 'block';
        });
    
        google.maps.event.addListener(this.gMap, 'zoom_changed', () => {
            searchButton.style.display = 'block';
        });
    }

    // Remove loadLocationsInView method as it's no longer needed
    loadLocationsInView(viewportData) {
        document.querySelector('[data-gmap]').classList.add('is-loading');
    
        jQuery.ajax({
            url: document.querySelector('[data-gmap]').dataset.url,
            type: 'POST',
            data: {
                action: 'get_store_locations',
                viewport: viewportData
            },
            success: (response) => {
                this.clearMarkers();
                if (response.data && response.data.length > 0) {
                    this.addMarkers(response.data);
                    this.updateClosestLocations();
                }
                document.querySelector('[data-gmap]').classList.remove('is-loading');
            }
        });
    }
    initDealersList() {
        const dealerItems = document.querySelectorAll('.dealer-item');
        
        dealerItems.forEach(item => {
            item.addEventListener('click', () => {
                const lat = parseFloat(item.dataset.lat);
                const lng = parseFloat(item.dataset.lng);
                
                if (lat && lng) {
                    // Center the map on the selected location
                    this.gMap.setCenter({ lat, lng });
                    this.gMap.setZoom(15);

                    // Find and trigger the corresponding marker
                    const marker = this.markers.find(m => 
                        m.getPosition().lat() === lat && 
                        m.getPosition().lng() === lng
                    );

                    if (marker) {
                        google.maps.event.trigger(marker, 'click');
                    }

                    // Add active class to selected item and remove from others
                    dealerItems.forEach(di => di.classList.remove('active'));
                    item.classList.add('active');
                }
            });
        });
    }
    initMap() {
        const mapElement = document.querySelector('[data-gmap]');
        if (!mapElement) return;
    
        const lat = parseFloat(mapElement.dataset.lat) || 50.8503; // Center of Belgium
        const lng = parseFloat(mapElement.dataset.lng) || 4.3517;
    
        const mapOptions = {
            center: { lat, lng },
            zoom: this.options.zoom,
            minZoom: this.options.minZoom,
            maxZoom: this.options.maxZoom,
            disableDefaultUI: true,
            styles: this.options.styles,
            // ... rest of map options
        };
        if (this.options.lockMap) {
            mapOptions.gestureHandling = 'none';
            mapOptions.draggable = false;
            mapOptions.scrollwheel = false;
            mapOptions.disableDoubleClickZoom = true;
        }
    
        this.gMap = new google.maps.Map(mapElement, mapOptions);
    
        // Only update the list on dragend, don't reload markers
        google.maps.event.addListener(this.gMap, 'dragend', () => {
            this.updateClosestLocations();
        });
    
        google.maps.event.addListener(this.gMap, 'zoom_changed', () => {
            this.updateClosestLocations();
        });
    }
    initGeolocation() {
            const geolocButton = document.getElementById('geolocation');
            if (!geolocButton) return;
            if (this.options.hideGeoloc) { geolocButton.style.display = 'none'; return; }
            
            geolocButton.style.display = 'block';
            geolocButton.classList.add('map-control-button');
            
            geolocButton.addEventListener('click', () => {
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((position) => {
                        const pos = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude
                        };
                        this.currentPosition = new google.maps.LatLng(pos.lat, pos.lng);
                        this.gMap.setCenter(pos);
                        this.gMap.setZoom(14);
                        this.loadLocations(pos);
                    });
                }
            });
    
            // Add the button to the map controls
            this.gMap.controls[google.maps.ControlPosition.TOP_RIGHT].push(geolocButton);
        }
    clearMarkers() {
        // Clear marker clusterer if it exists
        if (this.markerCluster) {
            this.markerCluster.clearMarkers();
            this.markerCluster = null;
        }

        // Clear individual markers
        if (this.markers.length) {
            this.markers.forEach(marker => {
                marker.setMap(null);
            });
            this.markers = [];
        }

        // Clear infowindows
        if (this.infowindows.length) {
            this.infowindows.forEach(infowindow => {
                infowindow.close();
            });
            this.infowindows = [];
        }

        // Reset bounds
        this.bounds = new google.maps.LatLngBounds();
    }
  
    updateClosestLocations() {
        const center = this.gMap.getCenter();
        
        // Calculate distances for all markers
        this.markers.forEach(marker => {
            const markerPosition = marker.getPosition();
            marker.distance = google.maps.geometry.spherical.computeDistanceBetween(
                markerPosition,
                center
            );
        });

        // Sort markers by distance
        const sortedMarkers = [...this.markers].sort((a, b) => a.distance - b.distance);
        
        // Take only the closest markers up to maxResults
        const closestMarkers = sortedMarkers.slice(0, this.options.maxResults);

        // Update the list in the sidebar
        const resultsContainer = document.querySelector('.dealer-locator__results');
        if (!resultsContainer) return;

        resultsContainer.innerHTML = '';

        closestMarkers.forEach(marker => {
            const locationData = marker.locationData;
            if (!locationData) return;

            const distance = Math.round(marker.distance / 1000); // Convert to kilometers

            const template = `
                <div class="dealer-item" data-lat="${locationData.Building.localization_lat}" data-lng="${locationData.Building.localization_lng}">
                    <div class="dealer-item__container">
                        ${locationData.Building.image ? 
                            `<div class="dealer-item__image">
                                <img src="${locationData.Building.image}" alt="${locationData.Building.title}">
                            </div>` : ''
                        }
                        <div class="dealer-item__content">
                            <h3>${locationData.Building.title}</h3>
                            ${locationData.Building.phone ? `<a href="tel:${locationData.Building.phone}" class="phone">${locationData.Building.phone}</a>` : ''}
                            <p class="address">${locationData.Building.street || ''} ${locationData.Building.postalcode || ''} <span>${locationData.Building.city || ''}</span></p>
                            <p class="distance">${distance} km</p>
                            ${locationData.Building.description ? 
                                `<p class="description">${locationData.Building.description}</p>` : ''
                            }
                        </div>
                    </div>
                </div>
            `;

            resultsContainer.insertAdjacentHTML('beforeend', template);
        });

        // Reinitialize click events for new items
        this.initDealersList();
    }
}


    
document.addEventListener('DOMContentLoaded', () => {
    // Check if the map element exists on the page
    const mapElement = document.querySelector('[data-gmap]');
    if (mapElement) {
        new DealerLocator({
            libraries: ['places', 'geometry'],
        });
    }
});